import { useQuery } from "@tanstack/react-query";
import { useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { CompanyDirectoryEntry } from "../types";

const MIN_CHARS = 3;
const MAX_RESULTS = 30;

// PostgREST `or=(...)` uses commas, parentheses and `*` as syntax: strip them
// from user input instead of trying to escape them.
const toPattern = (term: string) => term.replace(/[,()*\\%]/g, " ").trim();

const searchDirectory = async (term: string) => {
  const pattern = toPattern(term);
  const { data, error } = await getSupabaseClient()
    .from("company_directory")
    .select("*")
    .or(
      `name.ilike.*${pattern}*,tax_identifier.ilike.*${pattern}*,website.ilike.*${pattern}*`,
    )
    .order("name")
    .limit(MAX_RESULTS);
  if (error) throw error;
  return data as CompanyDirectoryEntry[];
};

/**
 * Company directory: every company in the CRM, but only whether it exists and
 * who handles it (view `company_directory`). History stays behind the
 * portfolio rule: a rep can only open their own companies.
 */
export const DirectoryPage = () => {
  const translate = useTranslate();
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(toPattern(term)), 300);
    return () => clearTimeout(timer);
  }, [term]);

  const enabled = debounced.length >= MIN_CHARS;
  const { data, isFetching, error } = useQuery({
    queryKey: ["company_directory", debounced],
    queryFn: () => searchDirectory(debounced),
    enabled,
  });

  return (
    <div className="mx-auto mt-4 flex max-w-3xl flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold">
          {translate("crm.construnario.directory.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.construnario.directory.intro")}
        </p>
      </div>
      <Input
        autoFocus
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={translate("crm.construnario.directory.search")}
      />
      {!enabled ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.construnario.directory.min_chars")}
        </p>
      ) : error ? (
        <p className="text-sm text-destructive">{String(error)}</p>
      ) : isFetching && !data ? null : !data?.length ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            {translate("crm.construnario.directory.no_results")}
          </p>
          <Button asChild size="sm">
            <Link to="/companies/create">
              {translate("crm.construnario.directory.create")}
            </Link>
          </Button>
        </div>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {data.map((entry) => (
              <DirectoryRow key={entry.id} entry={entry} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const DirectoryRow = ({ entry }: { entry: CompanyDirectoryEntry }) => {
  const translate = useTranslate();
  const details = [entry.tax_identifier, entry.city, entry.website]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{entry.name}</div>
        {details ? (
          <div className="truncate text-xs text-muted-foreground">
            {details}
          </div>
        ) : null}
      </div>
      <div className="text-right text-sm">
        <div className="text-xs text-muted-foreground">
          {translate("crm.construnario.directory.owner")}
        </div>
        <div>
          {entry.owner_name ?? translate("crm.construnario.directory.no_owner")}
          {entry.collaborator_names.length
            ? ` + ${entry.collaborator_names.join(", ")}`
            : ""}
        </div>
      </div>
      {entry.is_mine ? (
        <Button asChild size="sm" variant="outline">
          <Link to={`/companies/${entry.id}/show`}>
            {translate("crm.construnario.directory.open")}
          </Link>
        </Button>
      ) : (
        <Badge variant="secondary" className="invisible">
          {translate("crm.construnario.directory.open")}
        </Badge>
      )}
    </div>
  );
};

DirectoryPage.path = "/directory";
